import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SignaturePad from './SignaturePad';
import {
  DriverEvent,
  DriverStop,
  DriverTour,
  EventType,
  storage
} from './outbox';

import { API_BASE as BASE } from '../services/apiBase';

/** Razlozi izuzetka. Jedan tap, bez kucanja — vozač ovo radi na kiši. */
const EXCEPTIONS = ['Kupac nije kod kuće', 'Kupac odbio robu', 'Adresa netačna', 'Nema pristupa'];

/**
 * Razlozi kad roba treba da se uzme, a ne može. Drugi su od isporuke: ovde
 * kupca nema, nego prodavnice — i ako aparat nije tu, cela isporuka pada.
 */
const PICKUP_EXCEPTIONS = ['Aparata nema u prodavnici', 'Zatvoreno', 'Aparat oštećen'];

/** Razlozi promene redosleda. Ovo razdvaja šum od stvarnog znanja o terenu. */
const REORDER_REASONS = ['Radovi na putu', 'Kupac nedostupan', 'Kraći put', 'Drugo'];

interface Props {
  /** Ključ iz linka. Vozač se ne prijavljuje — telefon radi bez mreže. */
  accessKey: string;
}

/**
 * Vozačev ekran.
 *
 * Radi bez mreže od trenutka kad je tura učitana. Svaki potez ide prvo u lokalni
 * red pa tek onda, ako signala ima, na server — nikad obrnuto. Zbog toga nijedno
 * dugme ne čeka odgovor i nijedan zapis se ne gubi kad veza padne usred posla.
 */
/** Navigacija do stanice. Isti oblik i iz spiska i iz objašnjenja. */
function mapsLink(stop: DriverStop): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${stop.latitude},${stop.longitude}`;
}

export default function DriverApp({ accessKey }: Props) {
  // Kes se prihvata samo ako je tura učitana za OVOG vozača. Ranije se čitao
  // slepo, pa je svako ko otvori link video poslednju turu koja je na tom
  // uređaju otvorena — na službenom telefonu koji menja ruke, tuđu.
  const [tour, setTour] = useState<DriverTour | null>(
    () => (storage.tourDriverKey() === accessKey ? storage.loadTour() : null)
  );
  const [pending, setPending] = useState<DriverEvent[]>(() => storage.outbox());
  const [online, setOnline] = useState(navigator.onLine);
  /**
   * Otvorena stanica se pamti u telefonu.
   *
   * Vozač sa ovog ekrana odlazi u Mape, i telefon tada aplikaciju često ugasi u
   * pozadini. Kad se vrati, mora da nastavi tamo gde je stao — a ne da traži
   * svoju stanicu među četrnaest.
   */
  const [openStopId, setOpenStopId] = useState<number | null>(
    () => storage.openStop());

  useEffect(() => {
    storage.rememberOpenStop(openStopId);
  }, [openStopId]);

  /**
   * Da li je vozaču već objašnjeno kako se vraća sa Mapa.
   *
   * Navigacija otvara drugu aplikaciju preko cele slike i ovu sklanja. Ko to
   * nije video ranije, ne zna da je tura i dalje tu — pa se objašnjava jednom,
   * pre prvog odlaska, i pamti da je viđeno.
   */
  const [navigatingTo, setNavigatingTo] = useState<DriverStop | null>(null);
  const [signingStopId, setSigningStopId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  /** Šalje red na server. Briše lokalno samo ono što je server potvrdio. */
  const sync = useCallback(async (): Promise<void> => {
    const queue = storage.outbox();
    const current = storage.loadTour();
    if (queue.length === 0 || !current) return;

    setBusy(true);
    try {
      const response = await fetch(`${BASE}/api/driver/tours/${current.id}/sync?kljuc=${encodeURIComponent(accessKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: queue })
      });

      if (!response.ok) throw new Error(`Server je odbio paket (${response.status}).`);

      const result = await response.json();
      storage.forget(result.safeToForget ?? []);
      setPending(storage.outbox());

      setMessage(
        result.rejected > 0
          ? `${result.explanation} Odbijeno ostaje na telefonu — javi šefu magacina.`
          : null
      );
    } catch (e) {
      // Neuspeh sinhronizacije nije greška vozača i ne prekida mu posao.
      // Red ostaje netaknut i pokušaće se ponovo.
      setMessage(null);
    } finally {
      setBusy(false);
    }
  }, []);

  // Čim se signal vrati, red se prazni sam. Vozač ne mora ništa da pritisne.
  useEffect(() => {
    if (online && pending.length > 0) void sync();
  }, [online, pending.length, sync]);

  const loadTour = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(
        `${BASE}/api/driver/tours?kljuc=${encodeURIComponent(accessKey)}`);

      // Neispravan link i nedostupan server nisu ista stvar i ne smeju da
      // izgledaju isto: prvo se rešava porukom šefu, drugo čekanjem signala.
      if (response.status === 401) {
        setMessage('Ovaj link više ne važi. Traži nov od šefa magacina.');
        return;
      }

      const list = await response.json();
      if (!Array.isArray(list) || list.length === 0) {
        setMessage('Za danas nema ture na tebe.');
        return;
      }

      const full: DriverTour = await fetch(
        `${BASE}/api/driver/tours/${list[0].id}?kljuc=${encodeURIComponent(accessKey)}`)
        .then((r) => r.json());

      storage.saveTour(full, accessKey);
      setTour(full);
    } catch {
      setMessage('Nema veze sa serverom. Turu učitaj dok imaš signal, pre polaska.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Beleži potez: prvo lokalno stanje i red, pa tek onda pokušaj slanja.
   * Redosled je bitan — da veza padne baš u tom trenutku, potez je već sačuvan.
   */
  const record = (
    event: Omit<DriverEvent, 'clientEventId' | 'occurredAt'>,
    apply: (tour: DriverTour) => DriverTour
  ) => {
    if (!tour) return;

    const saved = storage.enqueue(event);
    const next = apply(tour);

    storage.saveTour(next, accessKey);
    setTour(next);
    setPending((q) => [...q, saved]);
  };

  const patchStop = (stopId: number, change: Partial<DriverStop>) => (current: DriverTour): DriverTour => ({
    ...current,
    stops: current.stops.map((s) => (s.id === stopId ? { ...s, ...change } : s))
  });

  const arrive = (stop: DriverStop) =>
    record({ type: EventType.ArrivedAtStop, stopId: stop.id },
      patchStop(stop.id, { actualArrival: new Date().toISOString() }));

  const depart = (stop: DriverStop) =>
    record({ type: EventType.DepartedFromStop, stopId: stop.id },
      patchStop(stop.id, { actualDeparture: new Date().toISOString() }));

  const deliver = (stop: DriverStop, itemId: number) =>
    record({ type: EventType.ItemDelivered, stopId: stop.id, stopItemId: itemId }, (current) => ({
      ...current,
      stops: current.stops.map((s) =>
        s.id === stop.id
          ? { ...s, items: s.items.map((i) => (i.id === itemId ? { ...i, isDelivered: true } : i)) }
          : s
      )
    }));

  const flagException = (stop: DriverStop, reason: string) =>
    record({ type: EventType.StopException, stopId: stop.id, text: reason },
      patchStop(stop.id, { exceptionReason: reason }));

  const addNote = (stop: DriverStop) => {
    const text = window.prompt('Beleška za ovu stanicu');
    if (!text?.trim()) return;

    record({ type: EventType.NoteAdded, stopId: stop.id, text: text.trim() },
      patchStop(stop.id, { notes: stop.notes ? `${stop.notes}\n${text.trim()}` : text.trim() }));
  };

  const sign = (stop: DriverStop, dataUrl: string) => {
    record({ type: EventType.SignatureCaptured, stopId: stop.id, signature: dataUrl },
      patchStop(stop.id, { hasSignature: true }));
    setSigningStopId(null);
  };

  /** Promena redosleda uvek nosi razlog. To je ceo smisao ovog zapisa. */
  const moveUp = (stop: DriverStop) => {
    if (!tour) return;

    const ordered = [...tour.stops].sort((a, b) => a.sequence - b.sequence);
    const index = ordered.findIndex((s) => s.id === stop.id);
    if (index <= 0) return;

    const reason = window.prompt(`Zašto menjaš redosled?\n\n${REORDER_REASONS.join(' · ')}`);
    if (!reason?.trim()) return;

    const above = ordered[index - 1];
    const newSequence = above.sequence;

    record({
      type: EventType.StopReordered,
      stopId: stop.id,
      sequence: newSequence,
      text: reason.trim()
    }, (current) => ({
      ...current,
      stops: current.stops.map((s) => {
        if (s.id === stop.id) return { ...s, sequence: newSequence };
        if (s.id === above.id) return { ...s, sequence: stop.sequence };
        return s;
      })
    }));
  };

  const startTour = () =>
    record({ type: EventType.TourStarted }, (current) => ({
      ...current,
      actualStart: new Date().toISOString(),
      status: 'InProgress'
    }));

  const finishTour = () =>
    record({ type: EventType.TourFinished }, (current) => ({
      ...current,
      actualEnd: new Date().toISOString(),
      status: 'Completed'
    }));

  const stops = useMemo(
    () => (tour ? [...tour.stops].sort((a, b) => a.sequence - b.sequence) : []),
    [tour]
  );

  const remaining = stops.filter((s) => !s.actualDeparture).length;

  if (!tour) {
    return (
      <div className="driver">
        <header className="driver__bar">
          <span>Vozač</span>
          <span className={online ? 'dot dot--on' : 'dot dot--off'}>{online ? 'mreža' : 'bez mreže'}</span>
        </header>

        <div className="driver__empty">
          <p>Turu učitaj dok imaš signal, pre polaska. Posle toga ti mreža ne treba.</p>
          <button type="button" className="primary" onClick={() => void loadTour()} disabled={busy}>
            {busy ? 'Učitavam…' : 'Učitaj današnju turu'}
          </button>

          {/* Tuđa tura se ne prikazuje, ali se ni ne prećutkuje: dok je njen red
              neposlat, ovaj telefon nosi tuđi posao i to niko ne sme da obriše
              slučajno. Sinhronizacija ga i dalje prazni sama. */}
          {pending.length > 0 && (
            <p className="driver__message">
              Na ovom telefonu stoji tura drugog vozača sa {pending.length} neposlatih
              zapisa. Šalju se sami čim ima signala — ne briši podatke pregledača.
            </p>
          )}

          {message && <p className="driver__message">{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="driver">
      <header className="driver__bar">
        <span>
          {tour.name} · {tour.vehicle}
        </span>
        <span className={online ? 'dot dot--on' : 'dot dot--off'}>{online ? 'mreža' : 'bez mreže'}</span>
      </header>

      {/* Broj nesinhronizovanih zapisa stoji stalno na ekranu. Vozač mora znati
          da nešto još nije otišlo, pre nego što ugasi telefon. */}
      {pending.length > 0 && (
        <div className="driver__pending">
          {pending.length} {pending.length === 1 ? 'zapis' : 'zapisa'} još nije poslato
          <button type="button" onClick={() => void sync()} disabled={busy || !online}>
            {busy ? 'Šaljem…' : 'Pošalji sad'}
          </button>
        </div>
      )}

      {message && <p className="driver__message">{message}</p>}

      {!tour.actualStart && (
        <button type="button" className="primary driver__start" onClick={startTour}>
          Krenuo sam — {tour.start.name}
        </button>
      )}

      {navigatingTo && (
        <div className="dnav">
          <div className="dnav__card">
            <h2>Otvara se navigacija</h2>

            <p>
              Mape se otvaraju preko cele slike i ovaj ekran se skloni. <strong>Tura
              nije nestala</strong> — čeka te.
            </p>

            <p className="dnav__how">
              Za povratak: pritisni dugme <strong>nazad</strong> na telefonu, ili
              dvaput dugme za prebacivanje aplikacija pa izaberi pregledač.
            </p>

            <p className="muted">
              Sve što si štiklirao je sačuvano u telefonu i ostaje sačuvano i bez
              signala.
            </p>

            <a
              className="dnav__go"
              href={mapsLink(navigatingTo)}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                storage.markNavigationExplained();
                setNavigatingTo(null);
              }}
            >
              Kreni, razumeo sam
            </a>

            <button type="button" className="link" onClick={() => setNavigatingTo(null)}>
              Ne još
            </button>
          </div>
        </div>
      )}

      <ol className="driver__stops">
        {stops.map((stop, index) => {
          const open = openStopId === stop.id;
          const done = stop.actualDeparture !== null;
          const delivered = stop.items.filter((i) => i.isDelivered).length;

          return (
            <li
              key={stop.id}
              className={`dstop${done ? ' dstop--done' : ''}`
                + `${stop.isPickup ? ' dstop--uzimanje' : ''}`}
            >
              <button
                type="button"
                className="dstop__head"
                onClick={() => setOpenStopId(open ? null : stop.id)}
              >
                <span className="dstop__no">{index + 1}</span>
                <span className="dstop__name">
                  {/* Preuzimanje se mora razlikovati na prvi pogled: sve ostalo
                      na ovom ekranu je isporuka, pa bi vozač inače pokušao da
                      ostavi robu tamo gde treba da je uzme. */}
                  {stop.isPickup && <span className="dstop__tag">UZIMA SE</span>}
                  {stop.name}
                  <span className="dstop__sub">
                    {stop.city ?? ''} · {delivered}/{stop.items.length}{' '}
                    {stop.isPickup ? 'utovareno' : 'isporučeno'}
                    {stop.requiresTwoPeople && <strong> · dva čoveka</strong>}
                  </span>
                </span>
                <span className="dstop__state">{done ? '✓' : open ? '▾' : '▸'}</span>
              </button>

              {open && (
                <div className="dstop__body">
                  {/* Prvo što se vidi kad se stanica otvori: tačka nije kapija.
                      Traženje brojeva u ulici koja ih nema je izgubljeno vreme,
                      a telefon je ovde jedino što dovodi do kupca. */}
                  {stop.withoutHouseNumber && (
                    <p className="dstop__bb">
                      <strong>BEZ BROJA (bb)</strong> — tačka je na ulici, ne pred kućom.
                      Pozovi kupca.
                    </p>
                  )}

                  {(stop.floor !== null || stop.accessNotes) && (
                    <p className="dstop__access">
                      {stop.floor !== null && stop.floor > 0 && (
                        <>
                          {stop.floor}. sprat{stop.hasLift ? ', ima lift' : ', bez lifta'}
                          {stop.accessNotes ? ' · ' : ''}
                        </>
                      )}
                      {stop.accessNotes}
                    </p>
                  )}

                  <div className="dstop__links">
                    {stop.latitude !== null && stop.longitude !== null && (
                      storage.navigationExplained() ? (
                        <a
                          href={mapsLink(stop)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Navigacija
                        </a>
                      ) : (
                        <button type="button" onClick={() => setNavigatingTo(stop)}>
                          Navigacija
                        </button>
                      )
                    )}

                    {/* Poziv radi i bez mreže — brojevi su učitani sa turom.
                        Kad ih ima više, drugi je nekog drugog ukućanina. */}
                    {stop.phones?.map((number, i) => (
                      <a key={number} href={`tel:${number}`}>
                        {stop.phones.length > 1 ? `Pozovi ${i + 1}` : 'Pozovi kupca'}
                      </a>
                    ))}
                  </div>

                  {!stop.actualArrival ? (
                    <button type="button" className="primary" onClick={() => arrive(stop)}>
                      Stigao sam
                    </button>
                  ) : (
                    <>
                      <ul className="dstop__items">
                        {stop.items.map((item) => (
                          <li key={item.id}>
                            <label>
                              <input
                                type="checkbox"
                                checked={item.isDelivered}
                                disabled={item.isDelivered}
                                onChange={() => deliver(stop, item.id)}
                              />
                              {item.articleName} <span className="muted">× {item.quantity}</span>
                            </label>
                          </li>
                        ))}
                      </ul>

                      {/* Preuzimanje niko ne potpisuje — roba se ne predaje
                          nego uzima, a potpis je dokaz o isporuci. */}
                      {!stop.isPickup && (stop.hasSignature ? (
                        <p className="dstop__signed">Potpis prikupljen ✓</p>
                      ) : (
                        <button type="button" onClick={() => setSigningStopId(stop.id)}>
                          Uzmi potpis
                        </button>
                      ))}

                      <button type="button" onClick={() => addNote(stop)}>Beleška</button>

                      {stop.exceptionReason ? (
                        <p className="dstop__exception">{stop.exceptionReason}</p>
                      ) : (
                        <div className="dstop__exceptions">
                          {(stop.isPickup ? PICKUP_EXCEPTIONS : EXCEPTIONS).map((reason) => (
                            <button key={reason} type="button" onClick={() => flagException(stop, reason)}>
                              {reason}
                            </button>
                          ))}
                        </div>
                      )}

                      {!stop.actualDeparture && (
                        <button type="button" className="primary" onClick={() => depart(stop)}>
                          Odlazim sa stanice
                        </button>
                      )}
                    </>
                  )}

                  {index > 0 && !stop.actualArrival && (
                    <button type="button" className="link" onClick={() => moveUp(stop)}>
                      Idem prvo ovde
                    </button>
                  )}

                  {stop.notes && <p className="dstop__notes">{stop.notes}</p>}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {tour.actualStart && !tour.actualEnd && remaining === 0 && (
        <button type="button" className="primary driver__finish" onClick={finishTour}>
          Završi turu — {tour.end.name}
        </button>
      )}

      {tour.actualEnd && (
        <div className="driver__done">
          <p>Tura završena.</p>
          {pending.length === 0 && (
            <button
              type="button"
              onClick={() => {
                storage.clearTour();
                setTour(null);
              }}
            >
              Skloni turu sa telefona
            </button>
          )}
        </div>
      )}

      {signingStopId !== null && (
        <SignaturePad
          onDone={(url) => {
            const stop = stops.find((s) => s.id === signingStopId);
            if (stop) sign(stop, url);
          }}
          onCancel={() => setSigningStopId(null)}
        />
      )}
    </div>
  );
}
